/**
 * Cadastro — empresa, contato, perfis, capacidades e vínculos (spec 02 §2 · telas 3.1–3.4).
 *
 * Três ausências valem mais que os campos presentes, e estão aqui de propósito:
 *
 * - **Não existe `setor`.** Nem em empresa, nem em contato. Ele é derivado de
 *   `tipo_empresa.setor` e chega como leitura (`setor` no tipo de SAÍDA), nunca como
 *   entrada. Aceitar setor num payload seria criar a segunda fonte que a P0-3 proíbe.
 * - **Não existe `papel`.** Cliente, Anunciante e Parceiro comissionado são papéis da
 *   VENDA (D-47). O que o cadastro guarda é CAPACIDADE — o que a parte pode ser.
 * - **Não existe "faturar contra"** (D-76). Os perfis de faturamento são multi-CNPJ da
 *   MESMA empresa; faturar contra terceiro deixou de existir.
 */
import { z } from 'zod'
import { dinheiroNaoNegativo, email, fracaoDeComissao, codigoDeMoeda, codigoDePais, dataNominal, limiteDoSeletor, listaDeUuidsNaUrl, uuid } from './comum.js'
import { endereco } from './entidades.js'

// ------------------------------------------------------------------ documento

/**
 * Documento normalizado: **sem máscara**, como o banco guarda.
 *
 * O formato (numérico ou alfanumérico) é derivável do valor pela presença de letra —
 * e por isso NÃO é campo. A tela alterna a máscara; o contrato recebe o valor limpo.
 * O dígito verificador é do servidor (`fn_documento_valido`), não daqui: replicar a
 * regra de DV em Zod criaria duas implementações do mesmo módulo 11, e uma delas
 * ficaria para trás quando a Receita mudar a tabela de pesos.
 */
export const documento = z
  .string()
  .trim()
  .regex(/^[A-Z0-9]+$/, 'O documento vai sem pontuação, só letras maiúsculas e números.')

// -------------------------------------------------------------------- empresa

export const tipoDeParceiro = z.enum(['Agência', 'Representante', 'Bureau'])
export const tipoDeComissao = z.enum(['Padrão', 'BV/Cashback'])
/**
 * Quando a comissão NASCE (D-174, DS-618): na emissão da NF autorizada ou na quitação da parcela. Dizia
 * quando ela liberava; a liberação agora é sempre a quitação da parcela do cliente, e "Venda" saiu.
 */
export const gatilhoDeComissao = z.enum(['emissao_nf', 'recebimento'])

export const capacidadeDeParceiro = z.object({
  tipoParceiroId: uuid,
  tipoParceiroNome: z.string(),
  tipoComissaoDefault: tipoDeComissao,
  percentualDefault: z.string().nullable(),
  valorFixoDefault: z.string().nullable(),
  moeda: codigoDeMoeda.nullable(),
  gatilhoDefault: gatilhoDeComissao,
  ativa: z.boolean(),
})
export type CapacidadeDeParceiro = z.infer<typeof capacidadeDeParceiro>

export const capacidadeDeFornecedor = z.object({
  id: uuid,
  categoria: z.string().nullable(),
  email: z.string().nullable(),
  telefone: z.string().nullable(),
  prazoPagamentoDias: z.number().int().nullable(),
  observacoes: z.string().nullable(),
  ativa: z.boolean(),
})
export type CapacidadeDeFornecedor = z.infer<typeof capacidadeDeFornecedor>

// ------------------------------------------------------------------ e-mails financeiros do perfil (D-186, DS-633)

/** Até 10 e-mails financeiros por perfil de faturamento. */
export const LIMITE_DE_EMAILS_FINANCEIROS = 10

/**
 * Um e-mail financeiro: a regra do contato (`email` de comum.ts), aparado e com até 254 caracteres. A caixa
 * digitada é preservada (o banco é citext e compara sem ela).
 */
export const emailFinanceiro = z.string().trim().max(254, { error: 'O e-mail passa de 254 caracteres.' }).pipe(email)

/**
 * A lista de e-mails financeiros do perfil: até 10, sem repetido sem diferença de maiúsculas. Nesta release é só
 * dado cadastral: nenhum envio a usa (decisão 6.11). O envio futuro lê a lista VIVA do perfil, nunca o retrato.
 */
export const emailsFinanceiros = z.array(emailFinanceiro)
  .max(LIMITE_DE_EMAILS_FINANCEIROS, { error: 'Até 10 e-mails por perfil.' })
  .superRefine((lista, ctx) => {
    const vistos = new Set<string>()
    lista.forEach((endereco, i) => {
      const chave = endereco.toLowerCase()
      if (vistos.has(chave)) ctx.addIssue({ code: 'custom', path: [i], message: 'Este e-mail já está na lista.' })
      vistos.add(chave)
    })
  })

export const perfilDeFaturamento = z.object({
  id: uuid,
  rotulo: z.string(),
  razaoSocial: z.string(),
  pais: codigoDePais,
  documento: z.string(),
  inscricaoEstadual: z.string().nullable(),
  inscricaoMunicipal: z.string().nullable(),
  endereco: endereco.nullable(),
  /** Os e-mails financeiros, na ordem e com a caixa digitadas (DS-633). */
  emailsFaturamento: z.array(z.string()),
  /** Transição (0194): a 1ª de `emailsFaturamento`, mantida pela ponte. Sai na contract. */
  emailFaturamento: z.string().nullable(),
  instrucoes: z.string().nullable(),
  primario: z.boolean(),
})
export type PerfilDeFaturamento = z.infer<typeof perfilDeFaturamento>

/**
 * Como a DV paga esta parte — `perfil_pagamento.metodo` (0058, com CHECK no banco).
 *
 * Mora no cadastro porque a coluna é do PERFIL. Quem a PREENCHE é a ficha 6.6; quem a
 * apagava era a 3.3, que regravava o perfil inteiro sem ela.
 */
export const metodoDePagamento = z.enum(['pix', 'ted', 'boleto', 'cambio'])
export type MetodoDePagamento = z.infer<typeof metodoDePagamento>

export const perfilDePagamento = z.object({
  id: uuid,
  rotulo: z.string(),
  titularNome: z.string(),
  titularDocumento: z.string().nullable(),
  banco: z.string().nullable(),
  agencia: z.string().nullable(),
  conta: z.string().nullable(),
  pixTipo: z.enum(['cnpj', 'cpf', 'email', 'telefone', 'aleatoria']).nullable(),
  pixChave: z.string().nullable(),
  /**
   * O método e a chave que ele exige — os dois vêm da 6.6 e a 3.3 apenas os CARREGA.
   *
   * Sem eles na SAÍDA o formulário do cadastro não tem como devolvê-los na atualização.
   * É por isso que o par está aqui e não só na entrada:
   * campo que não é lido não é preservado.
   */
  metodo: metodoDePagamento.nullable(),
  referencia: z.string().nullable(),
  emailAviso: z.string().nullable(),
  /**
   * D-103: como a DV paga DEPOIS da liberação — a política que a comissão CONGELA.
   *
   * Mora no perfil porque é do parceiro, não do pedido. NULO significa "não sei", e não
   * "no mesmo dia": sem política, a comissão futura não entra na curva de caixa com data
   * inventada. Ver DS-498 e a migration 0112.
   */
  prazoPolitica: z.enum(['dias_uteis', 'dia_fixo', 'fechamento_mensal']).nullable(),
  /** Quantos dias úteis, ou qual dia do mês. NULO em fechamento mensal e sem política. */
  prazoParametro: z.number().int().nullable(),
  primario: z.boolean(),
})
export type PerfilDePagamento = z.infer<typeof perfilDePagamento>

/** A linha da lista 3.1 — enxuta por decisão (D-43): sem valor financeiro nenhum. */
export const linhaDeEmpresa = z.object({
  id: uuid,
  nome: z.string(),
  nomeFantasia: z.string().nullable(),
  tipoEmpresaId: uuid.nullable(),
  tipoEmpresaNome: z.string().nullable(),
  /** DERIVADO de `tipo_empresa.setor`. `null` quando a empresa não tem tipo (DS-64). */
  setor: z.enum(['Público', 'Privado']).nullable(),
  contatos: z.number().int(),
  parceiroHabilitado: z.boolean(),
  fornecedorHabilitado: z.boolean(),
})
export type LinhaDeEmpresa = z.infer<typeof linhaDeEmpresa>

/**
 * O que o seletor com busca acrescenta a GET /empresas e GET /contatos (item 03 · DS-529).
 *
 * `limit` corta a página e faz o `total` contar o RECORTE inteiro; `excluir` tira ids da
 * resposta (o Cliente da busca do Anunciante, a própria empresa nas relacionadas). O
 * Contatos usa só o `limit`. Os dois são opcionais: sem eles a rota responde como antes.
 */
export const buscaDoSeletorNoCadastro = z.object({
  limit: limiteDoSeletor.optional(),
  excluir: listaDeUuidsNaUrl.optional(),
})
export type BuscaDoSeletorNoCadastro = z.infer<typeof buscaDoSeletorNoCadastro>

export const empresa = linhaDeEmpresa.extend({
  grupoEconomicoId: uuid.nullable(),
  grupoEconomicoNome: z.string().nullable(),
  pais: codigoDePais,
  documento: z.string().nullable(),
  pracaId: uuid.nullable(),
  pracaNome: z.string().nullable(),
  perfilPrazoOverrideId: uuid.nullable(),
  /** Dias efetivos: override, senão o padrão do tipo. `null` para empresa sem tipo. */
  prazoEfetivoDias: z.number().int().nullable(),
  /** Padrão vigente do tipo, mesmo quando o prazo efetivo tem override. */
  prazoPadraoDias: z.number().int().nullable(),
  inscricaoEstadual: z.string().nullable(),
  inscricaoMunicipal: z.string().nullable(),
  moedaPadrao: codigoDeMoeda.nullable(),
  endereco: endereco.nullable(),
  site: z.string().nullable(),
  observacoes: z.string().nullable(),
  ativa: z.boolean(),
  /**
   * "No cadastro desde" — data nominal no transporte, `MM/yyyy` na tela.
   *
   * Ela vem de `empresa.created_at`, que existe desde a 0009. Não é "Cliente desde":
   * ser cliente é papel da VENDA (D-47), e o que a ficha sabe é quando a empresa
   * entrou no cadastro.
   */
  criadaEm: dataNominal,
  /**
   * A data da exclusão, ou `null` — e é ela que separa dois estados que a ficha vinha
   * fundindo.
   *
   * A exclusão é SOFT (`deleted_at`). Sem este campo, abrir `?id=` de uma empresa
   * excluída caía em "Empresa não encontrada", afirmando a inexistência de um registro
   * que está lá — a mesma afirmação falsa que o bloco Financeiro foi ocultado para
   * evitar. Ler é permitido; escrever continua recusado pelo servidor.
   */
  excluidaEm: dataNominal.nullable(),
  capacidadeParceiro: capacidadeDeParceiro.nullable(),
  capacidadeFornecedor: capacidadeDeFornecedor.nullable(),
})
export type Empresa = z.infer<typeof empresa>

/**
 * Um dos três calendários de receita, no recorte de uma empresa.
 *
 * `valor` é `null` — e não `"0.00"` — quando quem pergunta não vê valores financeiros. A
 * diferença importa: zero é uma afirmação sobre o dinheiro, `null` é a ausência de
 * permissão para saber. A `quantidade` continua respondendo nos dois casos, porque contar
 * pedidos não é ver dinheiro.
 */
export const indicadorDaEmpresa = z.object({
  valor: z.string().nullable(),
  quantidade: z.number().int(),
})
export type IndicadorDaEmpresa = z.infer<typeof indicadorDaEmpresa>

/**
 * Os KPIs financeiros da ficha da empresa (3.2) — os quatro cartões do protótipo.
 *
 * **Dois eixos diferentes convivem aqui, e a spec 05 §3 é quem os separa.** Vendas,
 * Faturamento e Recebido respondem ao PERÍODO; `emAberto` e `aFaturar` são POSIÇÃO e não
 * respondem — é a exceção 2, e é a razão de o contrato os pôr fora do trio em vez de
 * fingir simetria. Uma tela que filtrasse os cinco pelo mesmo período responderia "o que
 * ficou em aberto naquele mês", que é outra pergunta e ninguém a fez.
 */
export const kpisDaEmpresa = z.object({
  periodoDe: dataNominal,
  periodoAte: dataNominal,
  vendas: indicadorDaEmpresa,
  faturamento: indicadorDaEmpresa,
  /** Fila de faturamento: parcelas com `faturar_em` alcançado e saldo sem NF. Posição, não período. */
  aFaturar: z.string().nullable(),
  recebido: indicadorDaEmpresa,
  /** Faturado e ainda não recebido. Posição, não período (spec 05 §3, exceção 2). */
  emAberto: z.string().nullable(),
})
export type KpisDaEmpresa = z.infer<typeof kpisDaEmpresa>

const camposDeEmpresa = {
  nome: z.string().trim().min(1, 'Informe a razão social.'),
  nomeFantasia: z.string().nullish(),
  // Opcional (DS-64): a empresa só-fornecedora não tem tipo. A trava de "sem tipo não
  // pode ser Cliente" mora no serviço da venda, onde o Setor passaria a fazer falta.
  tipoEmpresaId: uuid.nullish(),
  grupoEconomicoId: uuid.nullish(),
  pais: codigoDePais.default('BR'),
  // D-116: ausência de documento não identifica nem deduplica a empresa.
  documento: documento.nullish(),
  pracaId: uuid.nullish(),
  perfilPrazoOverrideId: uuid.nullish(),
  inscricaoEstadual: z.string().nullish(),
  inscricaoMunicipal: z.string().nullish(),
  moedaPadrao: codigoDeMoeda.nullish(),
  endereco: endereco.nullish(),
  site: z.string().nullish(),
  observacoes: z.string().nullish(),
}

export const entradaDeNovaEmpresa = z.object(camposDeEmpresa)
export type EntradaDeNovaEmpresa = z.infer<typeof entradaDeNovaEmpresa>

/*
 * O schema de EDIÇÃO tira o `.default()` antes de aplicar o `.partial()`, e a razão é
 * um defeito de verdade, achado ao escrever o passo 4.2.
 *
 * `.partial()` torna a chave opcional, mas **não desliga o `.default()`**: parsear
 * `{}` contra `z.object({ pais: codigoDePais.default('BR') }).partial()` devolve
 * `{ pais: 'BR' }`. Num `PATCH` que grava tudo o que não é `undefined`, isso não é
 * um detalhe — é o país sendo reescrito para BR em toda edição que não o mande.
 * Empresa estrangeira perderia o país no primeiro salvamento vindo de qualquer
 * superfície que não repita o campo, e o CHECK `fn_documento_valido(pais, documento)`
 * passaria a julgar um documento de fora pelas regras da Receita.
 *
 * A regra que fica: **campo com `.default()` não entra em schema de edição com o
 * default ligado.** Default é para o que NASCE; edição só grava o que veio.
 */
export const entradaDeEdicaoDeEmpresa = z.object({ ...camposDeEmpresa, pais: codigoDePais }).partial()
export type EntradaDeEdicaoDeEmpresa = z.infer<typeof entradaDeEdicaoDeEmpresa>

/*
 * Os tipos de ENTRADA saem de `z.input`, não de `z.infer`.
 *
 * A diferença importa por causa dos `.default()`: `z.infer` descreve o que o schema
 * DEVOLVE (país e primário já preenchidos, portanto obrigatórios), e o que a tela
 * precisa declarar é o que ela pode MANDAR — onde o campo com default é opcional.
 * Usar o tipo de saída como tipo de envio obrigaria todo formulário a repetir os
 * defaults que o contrato existe para centralizar.
 */
export const entradaDePerfilDeFaturamento = z.object({
  /** Presente na edição: a identidade é preservada por quem já referencia o perfil. */
  id: uuid.optional(),
  rotulo: z.string().trim().min(1, 'Dê um rótulo ao perfil (ex.: Matriz).'),
  razaoSocial: z.string().trim().min(1, 'Informe a razão social do perfil.'),
  pais: codigoDePais.default('BR'),
  documento: documento,
  inscricaoEstadual: z.string().nullish(),
  inscricaoMunicipal: z.string().nullish(),
  endereco: endereco.nullish(),
  /**
   * A regra de entrada da lista (DS-633), única mudança de semântica do perfil:
   * 1. `emailsFaturamento` presente é o estado final da lista, e `emailFaturamento` é ignorado;
   * 2. só `emailFaturamento` presente (o cliente antigo) governa a 1ª posição: igual, nada muda; outro, troca a 1ª;
   *    `null`, retira a 1ª;
   * 3. os dois ausentes: a lista do perfil existente é preservada.
   * Sem `.default([])`: ausente precisa continuar diferente de vazia.
   */
  emailsFaturamento: emailsFinanceiros.optional(),
  /** Transição (0194): o campo de um endereço, do cliente antigo. Validado; sai na contract. */
  emailFaturamento: emailFinanceiro.nullish(),
  instrucoes: z.string().nullish(),
  primario: z.boolean().default(false),
})

export const entradaDePerfilDePagamento = z.object({
  id: uuid.optional(),
  rotulo: z.string().trim().min(1, 'Dê um rótulo ao perfil.'),
  /* A tela chama o campo de "Nome do favorecido" (quem recebe, seja conta ou chave PIX); o
     nome técnico `titularNome` e a coluna `titular_nome` ficam como estão (DS-594). */
  titularNome: z.string().trim().min(1, 'Informe o nome do favorecido.'),
  titularDocumento: z.string().nullish(),
  banco: z.string().nullish(),
  agencia: z.string().nullish(),
  conta: z.string().nullish(),
  pixTipo: z.enum(['cnpj', 'cpf', 'email', 'telefone', 'aleatoria']).nullish(),
  pixChave: z.string().nullish(),
  /** Carregados e devolvidos intactos pela 3.3; quem os EDITA é a 6.6. */
  metodo: metodoDePagamento.nullish(),
  referencia: z.string().nullish(),
  emailAviso: z.string().nullish(),
  /* D-103: a política de prazo do parceiro. A comissão a CONGELA no fechamento do
     compromisso; editar aqui muda somente compromissos futuros (DS-498). */
  prazoPolitica: z.enum(['dias_uteis', 'dia_fixo', 'fechamento_mensal']).nullish(),
  prazoParametro: z.number().int().nullish(),
  primario: z.boolean().default(false),
})
  .refine(
    (p) => {
      if (p.prazoPolitica === null || p.prazoPolitica === undefined) return p.prazoParametro == null
      if (p.prazoPolitica === 'fechamento_mensal') return p.prazoParametro == null
      if (p.prazoPolitica === 'dias_uteis') return p.prazoParametro != null && p.prazoParametro >= 0 && p.prazoParametro <= 180
      return p.prazoParametro != null && p.prazoParametro >= 1 && p.prazoParametro <= 28
    },
    { message: 'A política de prazo e o parâmetro dela precisam combinar.', path: ['prazoParametro'] },
  )

/**
 * A capacidade de parceiro no fio (N-16, J-13): o corpo diz o que MUDA.
 *
 * - Chave AUSENTE mantém o que está gravado (na criação, vale o padrão: `Padrão`, `recebimento`,
 *   sem percentual, sem valor fixo, sem moeda). Era o `default('recebimento')` daqui que fazia religar
 *   uma capacidade com gatilho na emissão da NF voltar ao recebimento, e o `?? null` do upsert que
 *   apagava a moeda.
 * - `null` apaga (percentual, valor fixo, moeda).
 * - O valor fixo é dinheiro CANÔNICO (`1500.00`); zero é aceito e gravado como zerado intencional
 *   (J-13). `1.500` e `1.500,00` são recusados com o nome do campo: quem normaliza é a tela.
 */
export const entradaDeCapacidadeDeParceiro = z.object({
  tipoParceiroId: uuid,
  tipoComissaoDefault: tipoDeComissao.optional(),
  percentualDefault: fracaoDeComissao.nullish(),
  valorFixoDefault: dinheiroNaoNegativo.nullish(),
  moeda: codigoDeMoeda.nullish(),
  gatilhoDefault: gatilhoDeComissao.optional(),
  ativa: z.boolean().default(true),
})

export const entradaDeCapacidadeDeFornecedor = z.object({
  categoria: z.string().nullish(),
  email: z.string().nullish(),
  telefone: z.string().nullish(),
  /** Dias corridos para pagar depois da fatura, como na 6.6 (D-138): de 0 a 365. */
  prazoPagamentoDias: z.number().int().min(0).max(365).nullish(),
  observacoes: z.string().nullish(),
  ativa: z.boolean().default(true),
  /**
   * Reativar uma habilitação INATIVA é ato explícito (DS-535). Sem ele, o PUT sobre uma
   * capacidade inativa responde 409 `fornecedor.inativo`, e nada muda.
   */
  reativar: z.boolean().default(false),
})

export const entradaDeRelacionada = z.object({
  relacionadaId: uuid,
  observacao: z.string().nullish(),
})

/*
 * O Salvar da 3.3 num comando só (DS-593).
 *
 * A tela gravava a empresa e depois quatro blocos, cada um na sua transação: um bloco que
 * falhasse deixava os anteriores gravados. O comando recebe tudo o que a tela edita e grava
 * numa transação só, com as MESMAS regras de cada bloco (os schemas abaixo são os das rotas
 * de cada bloco, compostos, e não uma segunda definição).
 *
 * - `perfisFaturamento` e `perfisPagamento` são o estado FINAL de cada lista.
 * - `relacionadas` é compatibilidade temporária: ausente preserva tudo; presente, só o no-op
 *   legado é aceito. Atendimento novo usa atos explícitos por identidade/versão.
 * - `capacidadeParceiro: null` é "não mexer"; o objeto grava a capacidade (com `ativa: false`
 *   ela é desligada, preservando os termos).
 * - Na criação, `id` é a INTENÇÃO: o id que a empresa nova terá, sorteado pela tela na
 *   abertura do formulário. Repetir o comando com o mesmo `id` nunca cria a segunda empresa.
 */
const blocosDoCadastroDaEmpresa = {
  perfisFaturamento: z.array(entradaDePerfilDeFaturamento),
  perfisPagamento: z.array(entradaDePerfilDePagamento),
  capacidadeParceiro: entradaDeCapacidadeDeParceiro.nullable(),
  /** Ausente na SPA nova: Atendimento é gravado por identidade em atos próprios. */
  relacionadas: z.array(entradaDeRelacionada).optional(),
}

export const entradaDeNovoCadastroDeEmpresa = z.object({
  id: uuid,
  empresa: entradaDeNovaEmpresa,
  ...blocosDoCadastroDaEmpresa,
})

export const entradaDeEdicaoDoCadastroDeEmpresa = z.object({
  empresa: entradaDeEdicaoDeEmpresa,
  ...blocosDoCadastroDaEmpresa,
})

/**
 * O resultado do comando. `recuperada` é `true` quando a intenção já tinha sido gravada numa
 * tentativa anterior (a resposta dela se perdeu): nada foi escrito agora, e `empresa` é o
 * estado gravado, que a tela passa a mostrar.
 */
export const resultadoDoCadastroDeEmpresa = z.object({
  id: uuid,
  recuperada: z.boolean(),
  empresa,
})
export type ResultadoDoCadastroDeEmpresa = z.infer<typeof resultadoDoCadastroDeEmpresa>

/** Uma empresa relacionada, como a ficha 3.2 a lê (sem papel e sem % — D-47). */
export const empresaRelacionada = z.object({
  id: uuid,
  empresaId: uuid,
  nome: z.string(),
  observacao: z.string().nullable(),
})
export type EmpresaRelacionada = z.infer<typeof empresaRelacionada>

/**
 * Uma pessoa vinculada a uma empresa — o bloco "Contatos vinculados" da 3.2.
 *
 * O espelho de `vinculoDeContato`, visto do outro lado: lá a pergunta é "em que
 * empresas esta pessoa atua"; aqui é "quem trabalha nesta empresa". O **cargo é do
 * vínculo** nos dois (D-39), e por isso ele viaja nos dois — não é atributo da pessoa.
 */
export const contatoVinculado = z.object({
  contatoId: uuid,
  nome: z.string(),
  email: z.string().nullable(),
  cargoNome: z.string().nullable(),
  principal: z.boolean(),
  contatoFinanceiro: z.boolean(),
  /** O vínculo histórico permanece visível enquanto o contato está em soft delete. */
  excluido: z.boolean(),
})
export type ContatoVinculado = z.infer<typeof contatoVinculado>

/** Opções estritamente necessárias ao cadastro; não concede administração de Catálogos. */
export const tipoDeOpcaoDoCadastro = z.enum(['tipos-empresa', 'paises', 'moedas', 'pracas', 'perfis-prazo', 'tipos-parceiro'])
export type TipoDeOpcaoDoCadastro = z.infer<typeof tipoDeOpcaoDoCadastro>

export const atendimentoDaEmpresa = z.object({
  id: uuid,
  empresaId: uuid,
  nome: z.string(),
  ativa: z.boolean(),
  natureza: z.enum(['legado', 'atendimento']),
  direcao: z.enum(['presta', 'recebe', 'indefinida']),
  observacao: z.string().nullable(),
  versao: z.number().int().positive(),
})
export type AtendimentoDaEmpresa = z.infer<typeof atendimentoDaEmpresa>

export const entradaDeAtendimento = z.object({
  parceiraId: uuid,
  direcao: z.enum(['presta', 'recebe']),
  observacao: z.string().trim().max(2000).nullish(),
  /** Reaproveitamento deliberado de vínculo legado; nunca inferido da observação. */
  legadoId: uuid.optional(),
  versao: z.number().int().positive().optional(),
}).refine(e => (e.legadoId === undefined) === (e.versao === undefined), {
  path: ['versao'], message: 'Recarregue o vínculo antes de definir quem atende.',
})
export type EntradaDeAtendimento = z.input<typeof entradaDeAtendimento>
export const entradaDeEdicaoDeAtendimento = z.object({
  observacao: z.string().trim().max(2000).nullable(),
  versao: z.number().int().positive(),
})
export type EntradaDeEdicaoDeAtendimento = z.input<typeof entradaDeEdicaoDeAtendimento>

// -------------------------------------------------------------------- contato

/** Telefone: BR mascarado, ou internacional começando com `+` (preservado). */
export const telefone = z
  .string()
  .regex(/^(\(\d{2}\) \d{4,5}-\d{4}|\+[\d ()-]{6,20})$/, 'Use (00) 00000-0000 ou +55 51 99999-8888.')

export const canalDeComunicacao = z.enum(['whatsapp', 'email', 'telefone'])
export type CanalDeComunicacao = z.infer<typeof canalDeComunicacao>

export const vinculoDeContato = z.object({
  id: uuid,
  empresaId: uuid,
  empresaNome: z.string(),
  /** Setor DA EMPRESA, derivado do tipo dela. O contato não tem setor (P0-3). */
  empresaSetor: z.enum(['Público', 'Privado']).nullable(),
  cargoId: uuid.nullable(),
  cargoNome: z.string().nullable(),
  principal: z.boolean(),
  contatoFinanceiro: z.boolean(),
})
export type VinculoDeContato = z.infer<typeof vinculoDeContato>

export const optOut = z.object({
  canal: canalDeComunicacao,
  motivo: z.string().nullable(),
  desde: z.string(),
})
export type OptOut = z.infer<typeof optOut>

export const linhaDeContato = z.object({
  id: uuid,
  /** Nome COMPLETO. Continua sendo o que busca, ordenação, portal e comissões leem. */
  nome: z.string(),
  /** PÓS-V1-006. `null` no contato ainda não estruturado — o completo vive em `nome`. */
  primeiroNome: z.string().nullish(),
  sobrenome: z.string().nullish(),
  email: z.string().nullable(),
  /**
   * O documento pessoal INTEIRO e normalizado, do país de `pais` (D-149). O nome `cpf`
   * fica por compatibilidade.
   */
  cpf: z.string().nullable(),
  /** País de EMISSÃO do documento (PÓS-V1-007), para a tela saber como rotulá-lo. */
  pais: codigoDePais.default('BR'),
  aniversario: dataNominal.nullable(),
  empresaPrincipal: z.string().nullable(),
  /**
   * A empresa principal, para o nome abrir a ficha dela (D-136, DS-538). Nula quando a
   * empresa foi excluída, ainda que o nome continue aparecendo: a ficha responderia 404.
   */
  empresaPrincipalId: uuid.nullable(),
  cargoPrincipal: z.string().nullable(),
  parceiroHabilitado: z.boolean(),
})
export type LinhaDeContato = z.infer<typeof linhaDeContato>

export const contato = linhaDeContato.extend({
  telefones: z.array(z.string()),
  observacoes: z.string().nullable(),
  vinculos: z.array(vinculoDeContato),
  capacidadeParceiro: capacidadeDeParceiro.nullable(),
  capacidadeFornecedor: capacidadeDeFornecedor.nullable(),
  optOuts: z.array(optOut),
})
export type Contato = z.infer<typeof contato>

const camposDeContato = {
  /**
   * O nome COMPLETO, que continua obrigatório e continua sendo a fonte de busca.
   *
   * Quando as partes vêm informadas, o serviço compõe `nome` a partir delas — a tela não
   * precisa montar a frase, e duas telas não podem montá-la de jeitos diferentes.
   */
  nome: z.string().trim().min(1, 'Informe o nome.').optional(),
  /**
   * As partes — PÓS-V1-006. Nome obrigatório, Sobrenome opcional (decisão de produto:
   * nome sem sobrenome é cadastro legítimo).
   *
   * `undefined` significa **não toque nesta coluna**, e não "apague". É o que impede que
   * editar só o telefone de um contato antigo estruture silenciosamente o nome inteiro
   * como primeiro nome. É o mesmo idioma que o documento da edição usa.
   */
  primeiroNome: z.string().trim().min(1, 'Informe o nome.').nullish(),
  sobrenome: z.string().trim().nullish(),
  email: z.string().trim().pipe(z.email({ error: 'Informe um e-mail válido.' })).nullish(),
  /**
   * Entra sem máscara. A tela de edição mostra o documento inteiro (D-149) e o devolve
   * como está; o serviço normaliza. Valor com asterisco, que só chega de aba aberta antes
   * da D-149, é ignorado na edição (DS-119/DS-560).
   *
   * O nome `cpf` permanece por compatibilidade de contrato; o conteúdo é o documento
   * pessoal do país de `pais`.
   */
  cpf: z.string().nullish(),
  telefones: z.array(telefone).nullish(),
  aniversario: dataNominal.nullish(),
  pais: codigoDePais.default('BR'),
  observacoes: z.string().nullish(),
}

/**
 * Criar exige um nome — pelas PARTES ou pelo completo.
 *
 * A tela nova manda `primeiroNome` (+ `sobrenome` quando houver) e o serviço compõe o
 * `nome`. O completo continua aceito para quem já chamava a API assim: tirar essa porta
 * quebraria consumidor por uma mudança que é de interface.
 */
export const entradaDeNovoContato = z.object(camposDeContato).refine(
  (e) => (e.nome ?? '').trim() !== '' || (e.primeiroNome ?? '').trim() !== '',
  { error: 'Informe o nome.', path: ['primeiroNome'] },
)
export const entradaDeEdicaoDeContato = z.object({ ...camposDeContato, pais: codigoDePais }).partial()

export const entradaDeVinculo = z.object({
  empresaId: uuid,
  cargoId: uuid.nullish(),
  principal: z.boolean().default(false),
  contatoFinanceiro: z.boolean().default(false),
})

/**
 * O que se EDITA num vínculo que já existe — e note o que NÃO está aqui.
 *
 * `empresaId` não está porque ele é a chave: mudar de empresa é remover um vínculo e
 * criar outro, não editar este. E `principal` não está porque quem o decide é o
 * gatilho da migration 0010 com o índice `vinculo_principal_unico` — deixá-lo
 * editável por aqui poria uma terceira mão na regra que a DS-109 já mandou descer
 * para o banco.
 */
export const entradaDeVinculoEditado = z.object({
  cargoId: uuid.nullish(),
  contatoFinanceiro: z.boolean().default(false),
})

export const entradaDeOptOut = z.object({
  canal: canalDeComunicacao,
  motivo: z.string().trim().min(1, 'Diga por que este canal está sendo desligado.'),
})

export type EntradaDePerfilDeFaturamento = z.input<typeof entradaDePerfilDeFaturamento>
export type EntradaDePerfilDePagamento = z.input<typeof entradaDePerfilDePagamento>
export type EntradaDeCapacidadeDeParceiro = z.input<typeof entradaDeCapacidadeDeParceiro>
export type EntradaDeCapacidadeDeFornecedor = z.input<typeof entradaDeCapacidadeDeFornecedor>
export type EntradaDeRelacionada = z.input<typeof entradaDeRelacionada>
export type EntradaDeNovoCadastroDeEmpresa = z.input<typeof entradaDeNovoCadastroDeEmpresa>
export type EntradaDeEdicaoDoCadastroDeEmpresa = z.input<typeof entradaDeEdicaoDoCadastroDeEmpresa>
export type EntradaDeNovoContato = z.input<typeof entradaDeNovoContato>
export type EntradaDeEdicaoDeContato = z.input<typeof entradaDeEdicaoDeContato>
export type EntradaDeVinculo = z.input<typeof entradaDeVinculo>
export type EntradaDeVinculoEditado = z.input<typeof entradaDeVinculoEditado>
export type EntradaDeOptOut = z.input<typeof entradaDeOptOut>
